/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type NextApiRequest } from 'next';
import { type ContentStackHomepage, type FormattedContentStackHomepage } from '../../interface';
import { getContentByContentType } from '../common/getContentByContentType';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { deepSegmentationFilter } from '../../utils/deepSegmentationFilter';
import { cache } from '@/utils/serverQueryCache';
import { getHomePageCacheKey } from '../../utils/cache/getHomePageCacheKey';

const CACHE_TIME = 1000 * 60 * 5;

export const getContentStackHomePageContent = async (
  req: NextApiRequest | PageContextType['req'],
  query: ParsedUrlQuery,
  userSegments: string[] | undefined = [],
): Promise<FormattedContentStackHomepage> => {
  const notSegmentedDataCacheKey = getHomePageCacheKey([]);
  const cacheEnabled = process.env.CS_CACHE_ENABLED === 'true';

  let response: ContentStackHomepage | null = cache.getItem(notSegmentedDataCacheKey);

  if (!response) {
    response = await getContentByContentType<ContentStackHomepage>({
      contentTypeUid: 'homepage',
      referenceFieldPath: [
        'homepage_body.certona.certona',
        'homepage_body.featured_spotlight.feature_spotlight',
        'homepage_body.banner.banner',
        'homepage_body.banner_split.banner_split',
        'homepage_body.deals_split.deals_split',
        'homepage_body.ymme.ymme',
        'homepage_body.parttype_grid.parttype_grid',
        'homepage_body.hero_component.hero_component',
        'homepage_body.super_duper_hero.super_duper_hero',
        'homepage_body.live_text_hero.live_text_hero',
        'homepage_body.feature_carousel.feature_carousel',
        'homepage_body.horizontal_scroll_cards.horizontal_scroll_cards',
        'homepage_body.grid_banner.grid_banner',
        'homepage_body.prefooter.prefooter',
        'homepage_body.citrus_ad.citrus_ad',
        'homepage_body.target_div.target_div',
        'homepage_body.experience_injector.experience_injector',
        'homepage_body.blogtype_grid.blogtype_grid',
        'homepage_body.reviews_container.reviews_container',
        'homepage_body.reviews_container.reviews_container.fallback_banner',
        'homepage_body.certona_tabbed.certona_tabbed',
        'homepage_body.buy_again_container.buy_again_container',
        'homepage_body.buy_again_container.buy_again_container.fallback_banner',
        'homepage_body.pencil_banner.pencil_banner',
      ],
      req,
      query,
    });

    if (cacheEnabled) {
      cache.setItem(notSegmentedDataCacheKey, response, CACHE_TIME);
    }
  }

  let homepageBody = response.homepage_body ?? [];

  if (userSegments.length) {
    const segmentedDataCacheKey = getHomePageCacheKey(userSegments);
    let segmentedBody = cache.getItem<ContentStackHomepage['homepage_body']>(segmentedDataCacheKey);

    if (!segmentedBody) {
      segmentedBody = deepSegmentationFilter(homepageBody, userSegments) ?? [];
    }

    if (cacheEnabled) {
      cache.setItem(segmentedDataCacheKey, segmentedBody, CACHE_TIME);
    }

    homepageBody = segmentedBody;
  }

  return {
    homepageBody,
    seoTags: response.seo,
  };
};
